import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { ThunkApi } from '../../store'
import { WithRedirectBack } from '.'
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from './notificationActions'
import { AdServersRequestParams } from '../../api/nm-types'
import { AdServer, AdServerInit, AdServerUpdate, DeletedAdServer, ListResult } from 'common/api/v1/types'

export const listAdServers = createAsyncThunk<ListResult<AdServer>, AdServersRequestParams, ThunkApi>(
  'adServers/listAdServers',
  async (params, { dispatch, extra: { api } }) => {
    try {
      return await api.adServersApi.listAdServers(params)
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'fetch Ad Servers' }))
      throw error
    }
  },
)

export const getAdServer = createAsyncThunk<AdServer, string, ThunkApi>(
  'adServers/getAdServer',
  async (id, { dispatch, extra: { api } }) => {
    try {
      return await api.adServersApi.getAdServer(id)
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'fetch Ad Server' }))
      throw error
    }
  },
)

export const createAdServer = createAsyncThunk<AdServerInit, AdServer, ThunkApi>(
  'adServers/createAdServer',
  async (adServer, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const res = await api.adServersApi.createAdServer(adServer)
      navigate()(routes.adServers())
      dispatch(enqueueSuccessSnackbar('Ad Server created'))
      return res
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'create Ad Server' }))
      throw error
    }
  },
)

export const updateAdServer = createAsyncThunk<AdServer, AdServerUpdate, ThunkApi>(
  'adServers/updateAdServer',
  async (adServer, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const res = await api.adServersApi.updateAdServer(adServer.id, adServer)
      navigate()(routes.adServers())
      dispatch(enqueueSuccessSnackbar('Ad Server saved'))
      return res
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'update Ad Server' }))
      throw error
    }
  },
)

export const deleteAdServer = createAsyncThunk<DeletedAdServer, { id: string } & WithRedirectBack, ThunkApi>(
  'adServers/deleteAdServer',
  async ({ id, redirect }, { dispatch, extra: { api, routes, navigate } }) => {
    try {
      const res = await api.adServersApi.deleteAdServer(id)
      redirect && navigate()(routes.adServers())
      dispatch(enqueueSuccessSnackbar('Ad Server deleted'))
      return res
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'delete Ad Server' }))
      throw error
    }
  },
)

export const deleteAdServers = createAsyncThunk<string[], string[], ThunkApi>(
  'adServers/deleteAdServers',
  async (ids, { dispatch, extra: { api } }) => {
    try {
      for (const id of ids) {
        await api.adServersApi.deleteAdServer(id)
      }
      dispatch(enqueueSuccessSnackbar(`${ids.length} Ad Server${ids.length > 1 ? 's' : ''} deleted`))
      return ids
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'delete Ad Servers' }))
      throw error
    }
  },
)

export const clearAdServer = createAction('adServers/clearAdServer')
export const clearAdServers = createAction('adServers/clearAdServers')
